import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Live password strength + requirements, mirroring the backend's Django
 * AUTH_PASSWORD_VALIDATORS so the meter never says "strong" only for the server
 * to reject on submit:
 *   - MinimumLengthValidator (min_length 12)
 *   - NumericPasswordValidator (not all digits)
 *   - CommonPasswordValidator (a small client blocklist of obvious ones)
 *   - UserAttributeSimilarityValidator (not too similar to email/name)
 */

const COMMON = new Set([
  "password", "123456", "123456789", "12345678", "1234567890", "qwerty",
  "qwerty123", "111111", "000000", "abc123", "password1", "password123",
  "letmein", "welcome", "admin", "iloveyou", "monkey", "dragon", "qwertyuiop",
]);

export type PwCheck = { label: string; ok: boolean };

export type PwResult = { score: number; checks: PwCheck[]; valid: boolean };

export function evaluatePassword(
  pw: string,
  ctx: { email?: string; firstName?: string; lastName?: string } = {},
): PwResult {
  const lower = /[a-z]/.test(pw);
  const upper = /[A-Z]/.test(pw);
  const number = /\d/.test(pw);
  const symbol = /[^A-Za-z0-9]/.test(pw);
  const longEnough = pw.length >= 12;
  const notAllNumeric = pw.length > 0 && !/^\d+$/.test(pw);
  const notCommon = pw.length > 0 && !COMMON.has(pw.toLowerCase());

  const parts = [ctx.email?.split("@")[0], ctx.firstName, ctx.lastName]
    .filter(Boolean)
    .map((s) => (s as string).toLowerCase())
    .filter((s) => s.length >= 3);
  const lp = pw.toLowerCase();
  const notSimilar =
    pw.length === 0 || !parts.some((p) => lp.includes(p) || p.includes(lp));

  const checks: PwCheck[] = [
    { label: "At least 12 characters", ok: longEnough },
    { label: "Upper & lowercase letters", ok: lower && upper },
    { label: "At least one number", ok: number },
    { label: "At least one symbol", ok: symbol },
    { label: "Not all numbers", ok: notAllNumeric },
    { label: "Not a common password", ok: notCommon },
    { label: "Doesn't contain your name or email", ok: notSimilar },
  ];

  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (lower && upper && number) score++;
  if (symbol && pw.length >= 12) score++;
  if (!notAllNumeric || !notCommon) score = Math.min(score, 1);
  score = Math.max(0, Math.min(4, score));

  // The server will accept the password once the hard rules pass.
  const valid = longEnough && notAllNumeric && notCommon && notSimilar;

  return { score, checks, valid };
}

const BAR_COLORS = [
  "bg-rose-500",
  "bg-rose-500",
  "bg-amber-500",
  "bg-blue-500",
  "bg-emerald-500",
];
const LABELS = ["Too weak", "Weak", "Fair", "Good", "Strong"];
const LABEL_COLORS = [
  "text-rose-600 dark:text-rose-400",
  "text-rose-600 dark:text-rose-400",
  "text-amber-600 dark:text-amber-400",
  "text-blue-600 dark:text-blue-400",
  "text-emerald-600 dark:text-emerald-400",
];

export default function PasswordStrength({
  password,
  email,
  firstName,
  lastName,
}: {
  password: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}) {
  if (!password) return null;
  const { score, checks } = evaluatePassword(password, {
    email,
    firstName,
    lastName,
  });

  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-2.5">
      <div className="flex items-center gap-2">
        <div className="flex h-1.5 flex-1 gap-1">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={cn(
                "h-full flex-1 rounded-full",
                i < score ? BAR_COLORS[score] : "bg-muted-foreground/20",
              )}
            />
          ))}
        </div>
        <span className={cn("text-xs font-medium", LABEL_COLORS[score])}>
          {LABELS[score]}
        </span>
      </div>

      <ul className="grid gap-1">
        {checks.map((c) => (
          <li
            key={c.label}
            className={cn(
              "flex items-center gap-1.5 text-xs",
              c.ok ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground",
            )}
          >
            {c.ok ? (
              <Check className="size-3.5 shrink-0" />
            ) : (
              <X className="size-3.5 shrink-0 opacity-50" />
            )}
            {c.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
